import { MessageBlock } from '@deckee/deck-hand';
import { imageFormatStringCodec, sleep } from '@deckee/utilities';
import classNames from 'classnames/bind';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { callEndpoint, telemetry } from '../../../../services/backend';
import { captureEvent } from '../../../../services/event-capture';
import { getLayerDefinitions } from '../../../../services/map-layer';
import {
  ROUTE_MAP,
  ROUTE_MARKER_COMMENT_READ,
  ROUTE_MARKER_FLAG,
  ROUTE_MARKER_SUMMARY,
  ROUTE_NEW_MARKER_COMMENT,
  ROUTE_STORY_MARKER_EDIT,
} from '../../../../services/route/route-names';
import {
  transitionToNextRoute,
  transitionToRawURL,
} from '../../../../services/route/route-navigation';
import { getStore } from '../../../../services/store';
import {
  moveMapToLocation,
  selectUnitsConfig,
  setMarkerDetails,
} from '../../../../services/store/modules/app-controls';
import { selectMarkerTypeCategory } from '../../../../services/store/modules/marketplace';
import { getURLFromPath } from '../../../helpers/share-helpers';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import HeaderBar from '../../../layouts/components/HeaderBar/HeaderBar';
import HeaderBarButton from '../../../layouts/components/HeaderBarButton/HeaderBarButton';
import { close, pencil } from '../../../shared/vector-images';
import styles from './MarkerContainer.scss';
import MarkerContainerView from './MarkerContainerView';

const cx = classNames.bind(styles);
class MarkerContainer extends React.Component {
  constructor(props) {
    super(props);

    const { markerType, id } = this.props;
    this.state = {
      isLoading: false,
      error: '',

      flaggedForReview: false,
      allowEditing: false,

      formattedLatLon: null,
      markerIcon: null,
      markerIconDiscColour: null,
      eventDetails: null,
      markerIconImageAddress: null,

      checkboxes: [],
      title: '',
      descriptionHeaderText: 'Information',
      description: null,
      customSummaryTitle: null,
      customDescription: null,
      image: null,
      informationButtons: [],
      addedOrEditedByLabel: null,

      allowComments: false,
      numberOfComments: null,
      buttonHref: null,
      buttonLabel: null,
    };

    this.markerDefinitions = getLayerDefinitions()[markerType];

    const markerHasAMarketplaceCategory = selectMarkerTypeCategory(
      getStore().getState(),
      markerType,
    );

    // If the markerType is part of a category, then redirect to a category page
    if (markerHasAMarketplaceCategory) {
      transitionToRawURL(`/maps/marketplace/listing/${id}`);
    }

    // Marker comments actions
    // Add allowComments check
    this.addComments = () => {
      captureEvent({
        type: 'marker: add comment',
        payload: { markerType, id },
      });
      transitionToNextRoute(ROUTE_NEW_MARKER_COMMENT, {
        markerType,
        id,
      });
    };
    this.readComments = () => {
      captureEvent({
        type: 'marker: read comment',
        payload: { markerType, id },
      });
      transitionToNextRoute(ROUTE_MARKER_COMMENT_READ, {
        markerType,
        id,
      });
    };

    this.editButtonAction = () => {
      captureEvent({
        type: 'marker: edit',
        payload: { markerType, id },
      });
      if (markerType !== 'storyMarker') {
        transitionToNextRoute(ROUTE_MARKER_FLAG, { markerType, id });
      } else {
        transitionToNextRoute(ROUTE_STORY_MARKER_EDIT, { id });
      }
    };

    // Setup the share and comment actions
    this.markerSharingPath = `marker/summary/${markerType}/${id}`;
    this.markerSharingText = this.markerDefinitions?.shareText
      ? this.markerDefinitions.shareText
      : `Check out this ${this.markerDefinitions.displayName} on Deckee!`;

    this.handleCallEndpoint = async (endpoint, entityId) => {
      // Attempt to get marker with exponential back-off.
      let data;
      let delay = 0;
      let error;
      while (!data && delay < 10000) {
        /* eslint-disable-next-line no-await-in-loop */
        await sleep(delay);
        try {
          /* eslint-disable-next-line no-await-in-loop */
          data = await callEndpoint(endpoint, { id: entityId });
        } catch (currentError) {
          error = currentError;
        }
        delay = delay ? delay * 2 : 1000;
      }

      if (!data) {
        telemetry(
          telemetry.WARN,
          `[handleCallEndpoint] failed for endpoint: ${endpoint}`,
        );
        this.setState(() => ({
          isLoading: false,
          error: error.message,
        }));
        return null;
      }
      return data;
    };
  }

  componentDidMount() {
    const { markerType, id, dispatchSetMarkerDetails } = this.props;

    dispatchSetMarkerDetails(markerType, id);
    this.mounted = true;
    this.getItem();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  async getItem() {
    telemetry(telemetry.DEBUG, '[MarkerContainer.getItem] called');
    if (!this.mounted) return;
    this.setState(() => ({
      isLoading: true,
    }));

    const {
      id,
      dispatchMoveMapToLocation,
      currentMapZoom,
      markerType,
      currentMember,
    } = this.props;
    const { error } = this.state;
    let flaggedForReview = false;
    let allowEditing = false;
    let markerTitle;
    let checkboxes;
    let informationButtons;
    let banner;
    let backgroundImage;
    let iconImage;
    let descriptionHeaderText = 'Information';
    let customDescription;
    let customSummaryTitle;
    let addedOrEditedByLabel;
    let markerNumberOfComments;
    let eventDetails;
    let buttonHref;
    let buttonLabel;

    // Get the item
    const item = await this.handleCallEndpoint(
      callEndpoint.endpoints.mapSearchQueryById,
      id,
    );
    if (item.hidden === true) {
      // If marker is hidden, send back to popover to show message
      transitionToNextRoute(ROUTE_MARKER_SUMMARY, { markerType, id });
    }
    telemetry(
      telemetry.DEBUG,
      `[MarkerContainer.getItem] received item ${JSON.stringify(item)}`,
    );

    let story;
    let description;
    let image;

    // Get the marker icon config
    let markerIcon = this?.markerDefinitions?.icon;
    let markerIconImageAddress = null;

    if (markerType === 'storyMarker') {
      markerIcon = null;

      const imageHash = imageFormatStringCodec.encode({
        resize: {
          width: 200,
          height: 200,
        },
      });

      markerIconImageAddress = `/public/app-main/image/${item.owningInfluencer.imageId}--${imageHash}.jpg`;

      // Get influencer stories
      const { stories } = await this.handleCallEndpoint(
        callEndpoint.endpoints.getMemberStories,
        item.owningInfluencer.id,
      );
      story = stories;
      telemetry(
        telemetry.DEBUG,
        `[MarkerContainer.getItem] received story ${JSON.stringify(story)}`,
      );

      allowEditing = !!(
        currentMember && currentMember.id === item.owningInfluencer.id
      );
    }

    const markerOptions =
      this.markerDefinitions && this.markerDefinitions.standardMarkerOptions;

    const markerViewOptions = markerOptions && markerOptions.viewOptions;

    // Add the marker indicator disc colour
    const markerIconDiscColour = item?.discColour || null;

    // Format the lat and lon
    const formattedLat = item && +item.latLon.lat.toFixed(4);
    const formattedLon = item && +item.latLon.lon.toFixed(4);

    const allowComments = markerOptions && markerOptions.allowComments;

    if (item) {
      const {
        flaggedAsDuplicate,
        flaggedAsIncorrect,
        editable,
        title,
        addedToMapBy,
        numberOfComments,
      } = item;

      let { createdAt, lastEditedAt, content } = item;

      if (item.description) {
        description = item.description;
      }

      if (item.image) {
        image = item.image;
      }

      // Determine if the marker has been flagged for review
      if (flaggedAsDuplicate || flaggedAsIncorrect) {
        flaggedForReview = true;
      }

      // Determine if the marker can be edited
      if (editable && !flaggedAsDuplicate && !flaggedAsIncorrect) {
        allowEditing = true;
      }

      // Format the marker title
      markerTitle = title || (markerViewOptions && markerViewOptions.typeTitle);

      if (content) {
        checkboxes = content.checkboxes;
        customDescription = content.customDescription || null;
        customSummaryTitle = content?.customSummaryTitle || null;
        informationButtons =
          content.links &&
          content.links.filter((link) => link.type === 'informationButton');
        banner = content.banner || null;
        backgroundImage = content.backgroundImage || null;
        iconImage = content.iconImage || null;
        eventDetails = content.eventDetails || null;
        buttonHref = content?.buttonHref || null;
        buttonLabel = content?.buttonLabel || null;
      }

      if (story) {
        lastEditedAt = story.lastEditedAt;
        content = story.content;
        createdAt = story.createdAt;

        // Add the story title if stories found
        if (story.title) {
          descriptionHeaderText = story.title;
        }

        description = story.description;
        image = story.image;
        customDescription = story.content && story.content.customDescription;
        informationButtons =
          story &&
          story.content &&
          story.content.links &&
          story.content.links.filter(
            (link) => link.type === 'informationButton',
          );
        if (
          story.description &&
          story.content &&
          story.content.customDescription
        ) {
          customDescription.unshift({
            type: 'paragraph',
            content: story.description,
          });
          description = null;
        }
      }

      // Format the added or edited by label
      if (lastEditedAt) {
        addedOrEditedByLabel = `Last updated ${moment(lastEditedAt).fromNow()}`;
      }
      if (createdAt && !lastEditedAt) {
        addedOrEditedByLabel = `Added ${moment(createdAt).fromNow()}`;

        // Add the username if available // TODO: add in later
        if (addedToMapBy && addedToMapBy.title) {
          addedOrEditedByLabel += ` by ${addedToMapBy.title}`;
        }
      }
      if (numberOfComments) {
        markerNumberOfComments = numberOfComments;
      }
    }

    if (this.mounted) {
      this.setState(() => ({
        flaggedForReview,
        allowEditing,
        formattedLatLon: {
          lat: formattedLat,
          lon: formattedLon,
        },
        markerIcon,
        markerIconDiscColour,
        markerIconImageAddress,
        checkboxes,
        title: markerTitle,
        descriptionHeaderText,
        description,
        customSummaryTitle,
        customDescription,
        eventDetails,
        banner,
        backgroundImage,
        iconImage,
        image,
        informationButtons,
        addedOrEditedByLabel,
        allowComments,
        isLoading: !item && !error,
        numberOfComments: markerNumberOfComments,
        metaTagURL: getURLFromPath(`marker/view/${markerType}/${id}`),
        buttonHref,
        buttonLabel,
      }));

      // update the location?
      telemetry(
        telemetry.DEBUG,
        `[MarkerContainer] calling move to map with ${JSON.stringify(
          item.latlon,
        )}`,
      );
      await dispatchMoveMapToLocation({
        center: {
          lat: +item.latLon.lat,
          lon: +item.latLon.lon,
        },
        zoom: currentMapZoom,
      });
    }
  }

  render() {
    const { id, markerType, currentMember } = this.props;

    const {
      isLoading,
      error,
      flaggedForReview,
      allowEditing,
      formattedLatLon,
      markerIcon,
      markerIconDiscColour,
      markerIconImageAddress,
      checkboxes,
      title,
      descriptionHeaderText,
      description,
      customSummaryTitle,
      customDescription,
      banner,
      image,
      informationButtons,
      addedOrEditedByLabel,
      allowComments,
      numberOfComments,
      metaTagURL,
      backgroundImage,
      iconImage,
      eventDetails,
      buttonHref,
      buttonLabel,
    } = this.state;

    // Setup our right side header buttons
    const rightNodeButtons = [
      <HeaderBarButton
        key="button-close"
        ariaLabel="close"
        icon={close()}
        onClick={() => transitionToNextRoute(ROUTE_MAP)}
      />,
    ];
    if (allowEditing) {
      rightNodeButtons.unshift(
        <HeaderBarButton
          key="button-edit"
          ariaLabel="edit"
          icon={pencil()}
          onClick={() => this.editButtonAction()}
        />,
      );
    }

    // the main image for the post is used for the cover image if there isn't a backgroundImage supplied on the marker. Also, the `image`
    // return in the marker api call is an id, so we need to manually form the image URL
    const coverImage =
      backgroundImage || (image ? `/public/app-main/image/${image}` : null);
    const mainImage = backgroundImage ? image : null;

    const markerConfig = this.markerDefinitions;

    const getDrawerTitle = () => {
      if (eventDetails) return 'Event';

      if (customSummaryTitle) return customSummaryTitle;
      return markerConfig.displayName || title;
    };

    return (
      <BasicDrawerLayout
        backgroundImage={coverImage}
        isLoading={!error && isLoading}
        renderHeaderBar={
          <HeaderBar
            solidBackground
            centerNode={getDrawerTitle()}
            rightNode={rightNodeButtons}
          />
        }
      >
        {error && (
          <div className={cx('marker-container__error')}>
            <MessageBlock type="error">
              {error.message || 'Marker not found.'}
            </MessageBlock>
          </div>
        )}

        {!isLoading && !error && (
          <MarkerContainerView
            id={id}
            markerType={markerType}
            editRoute={this.editButtonAction}
            flaggedForReview={flaggedForReview}
            allowEditing={allowEditing}
            currentMember={currentMember}
            formattedLatLon={formattedLatLon}
            markerIcon={markerIcon}
            markerIconDiscColour={markerIconDiscColour}
            markerIconImageAddress={markerIconImageAddress}
            checkboxes={checkboxes}
            headerImage={iconImage}
            title={title}
            eventDetails={eventDetails}
            descriptionHeaderText={descriptionHeaderText}
            description={description}
            customSummaryTitle={customSummaryTitle}
            customDescription={customDescription}
            banner={banner}
            image={mainImage}
            informationButtons={informationButtons}
            addedOrEditedByLabel={addedOrEditedByLabel}
            sharePath={this.markerSharingPath}
            shareText={this.markerSharingText}
            allowComments={allowComments}
            readCommentsAction={this.readComments}
            addCommentsAction={this.addComments}
            numberOfComments={numberOfComments}
            metaTagURL={metaTagURL}
            buttonHref={buttonHref}
            buttonLabel={buttonLabel}
          />
        )}
      </BasicDrawerLayout>
    );
  }
}

MarkerContainer.propTypes = {
  currentMember: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    image: PropTypes.string,
  }),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  markerType: PropTypes.string.isRequired,
  currentMapZoom: PropTypes.number.isRequired,
  dispatchMoveMapToLocation: PropTypes.func.isRequired,
  dispatchSetMarkerDetails: PropTypes.func.isRequired,
};

MarkerContainer.defaultProps = {
  currentMember: undefined,
};

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  dispatchMoveMapToLocation: async (newLocation) =>
    dispatch(await moveMapToLocation(newLocation)),
  dispatchSetMarkerDetails: (mapMarkerType, selectedMarkerId) =>
    dispatch(
      setMarkerDetails({
        mapMarkerType,
        selectedMarkerId,
      }),
    ),
});

const mapStateToProps = (state) => ({
  currentMember: state.backendService.currentMember,
  currentMapZoom: state.appControls.currentMapParams.viewPortDetails.zoom,
  coordinateSetting: selectUnitsConfig(state)?.coordinate,
});

export default connect(mapStateToProps, mapDispatchToProps)(MarkerContainer);
